import {UICanvas} from './uiCanvas';

export class UIManager {
  public static canvas: UICanvas = null;
  public static htmlCanvas: HTMLCanvasElement = null;
  public static ctx: CanvasRenderingContext2D = null;
  public static canvasScale: number = 1;
  public static isMobile: boolean = false;

  public static get checkMobile(): boolean {
    try{ document.createEvent("TouchEvent"); return true; }
    catch(e){ return false; }
  }

  protected static lastCanvasWidth: number = 0;
  protected static lastCanvasHeight: number = 0;
  protected static lastUpdateTime: number = 0;
  protected static updateTimer: number = 0;
  protected static frameTime: number = 0;
  protected static touchDowns: any[] = [];
  protected static totalUpdateTime: number = 0;
  protected static totalUpdates: number = 0;

  public static connect(htmlCanvas: HTMLCanvasElement): void {
    UIManager.htmlCanvas = htmlCanvas;
    UIManager.ctx = htmlCanvas.getContext('2d');
    UIManager.canvas = new UICanvas();
    UIManager.canvas.size = {x : htmlCanvas.width, y : htmlCanvas.height};
    UIManager.isMobile = UIManager.checkMobile;
    UIManager.lastCanvasWidth = 0;
    UIManager.lastCanvasHeight = 0;
    UIManager.lastUpdateTime = 0;
    UIManager.updateTimer = 0;
    UIManager.frameTime = 0;
    UIManager.touchDowns = [];
    UIManager.totalUpdateTime = 0;
    UIManager.totalUpdates = 0;

    if (UIManager.isMobile) {
      htmlCanvas.addEventListener('touchstart', UIManager.onTouchStart);
      htmlCanvas.addEventListener('touchend', UIManager.onTouchEnd);
      htmlCanvas.addEventListener('touchmove', UIManager.onTouchMove);
    }
    else {
      htmlCanvas.addEventListener('mousedown', UIManager.onMouseDown, false);
      htmlCanvas.addEventListener('mouseup', UIManager.onMouseUp, false);
      htmlCanvas.addEventListener('mousemove', UIManager.onMouseMove, false);
      htmlCanvas.addEventListener('wheel', UIManager.onMouseWheel, false);
      htmlCanvas.addEventListener("contextmenu", e => e.preventDefault());
    }

    let fr = window.localStorage.getItem('CanvasFrameRate');
    if(fr)
      UIManager.setFrameRate(+fr);

    UIManager.resizeCanvas();
    UIManager.update();
  }

  public static disconnect() {
    if (UIManager.isMobile) {
      UIManager.htmlCanvas?.removeEventListener('touchstart', UIManager.onTouchStart, false);
      UIManager.htmlCanvas?.removeEventListener('touchend', UIManager.onTouchEnd, false);
      UIManager.htmlCanvas?.removeEventListener('touchmove', UIManager.onTouchMove, false);
    }
    else {
      UIManager.htmlCanvas?.removeEventListener('mousedown', UIManager.onMouseDown, false);
      UIManager.htmlCanvas?.removeEventListener('mouseup', UIManager.onMouseUp, false);
      UIManager.htmlCanvas?.removeEventListener('mousemove', UIManager.onMouseMove, false);
      UIManager.htmlCanvas?.removeEventListener('wheel', UIManager.onMouseWheel, false);
    }

    UIManager.touchDowns = [];

    UIManager.canvas.removeCurrentScreen();
    UIManager.canvas = null;
    UICanvas.instance = null;

    UIManager.htmlCanvas = null;
    UIManager.ctx = null;
  }

  public static update(): void {
    if(!UIManager.ctx || !UIManager.canvas)
      return;

    let now = performance.now();
    if(UIManager.lastUpdateTime == 0)
      UIManager.lastUpdateTime = now;

    let elapsed = now - UIManager.lastUpdateTime;

    UIManager.updateTimer += elapsed;
    UIManager.lastUpdateTime = now;

    if(UIManager.frameTime == 0 || UIManager.updateTimer == 0 || UIManager.updateTimer >= UIManager.frameTime) {
      UIManager.ctx.clearRect(0, 0, UIManager.canvas.width, UIManager.canvas.height);
      UIManager.resizeCanvas();
      UIManager.canvas.update();

      UIManager.totalUpdates++;
      if(UIManager.frameTime == 0)
        UIManager.totalUpdateTime += elapsed;
      else
        UIManager.totalUpdateTime += UIManager.frameTime;

      if(UIManager.updateTimer > 0)
        UIManager.updateTimer -= UIManager.frameTime;
    }

    requestAnimationFrame(UIManager.update);
  }

  public static setFrameRate(rate:number) {
    window.localStorage.setItem('CanvasFrameRate', rate.toString());
    UIManager.frameTime = (rate == 0) ? 0 : (1000/rate);
    UIManager.updateTimer = 0;
    UIManager.totalUpdateTime = 0;
    UIManager.totalUpdates = 0;
  }

  public static getFrameRate(): number {
    if(UIManager.frameTime == 0)
      return 0;
    return Math.round(1000 / UIManager.frameTime);
  }

  public static getAverageFrameRate(): number {
    if(UIManager.totalUpdateTime == 0)
      return 0;
    return (UIManager.totalUpdates / (UIManager.totalUpdateTime / 1000));
  }

  public static resizeCanvas():boolean {
    if (UIManager.ctx == null)
      return false;

    let windowWidth = window.innerWidth;
    let windowHeight = window.innerHeight;
  
    if(UIManager.htmlCanvas.width == windowWidth && UIManager.htmlCanvas.height == windowHeight)
      return false;

    let contentScale = 1 / window.devicePixelRatio;
    let viewport = document.getElementById('viewport');
    if(viewport)
      viewport.setAttribute('content', `initial-scale=${contentScale}, user-scalable=0, minimum-scale=${contentScale}, maximum-scale=${contentScale}`);

    UIManager.htmlCanvas.width = windowWidth;
    UIManager.htmlCanvas.height = windowHeight;

    UIManager.canvasScale = 1;

    let iw = window.innerWidth / window.devicePixelRatio;
    let ih = window.innerHeight / window.devicePixelRatio;

    if(UIManager.isMobile) {
      if(iw >= 768 && ih >= 768)
        UIManager.canvasScale = ih / 768;
      else if(iw > ih)
        UIManager.canvasScale = ih / 600;
    else
        UIManager.canvasScale = iw / 375;
    }
    else //if(ih >= 768)
      UIManager.canvasScale = ih / 768;

    UIManager.canvasScale *= window.devicePixelRatio;
    UIManager.canvasScale = 1 / UIManager.canvasScale;

    UIManager.ctx.scale(1/UIManager.canvasScale, 1/UIManager.canvasScale);

    UIManager.canvas.size.x = window.innerWidth * UIManager.canvasScale;
    UIManager.canvas.size.y = window.innerHeight * UIManager.canvasScale;

    return true;
  }

  public static onMouseDown(event: MouseEvent) {
    var sx = (event.x - UIManager.htmlCanvas.offsetLeft) * UIManager.canvasScale;
    var sy = (event.y - UIManager.htmlCanvas.offsetTop) * UIManager.canvasScale;
    UIManager.canvas.onSystemMouseDown(sx, sy);
  }

  public static onMouseUp(event: MouseEvent) {
    var sx = (event.x - UIManager.htmlCanvas.offsetLeft) * UIManager.canvasScale;
    var sy = (event.y - UIManager.htmlCanvas.offsetTop) * UIManager.canvasScale;
    UIManager.canvas.onSystemMouseUp(sx, sy);
  }

  public static onMouseMove(event: MouseEvent) {
    var sx = (event.x - UIManager.htmlCanvas.offsetLeft) * UIManager.canvasScale;
    var sy = (event.y - UIManager.htmlCanvas.offsetTop) * UIManager.canvasScale;
    UIManager.canvas.onSystemMouseMove(sx, sy);
  }

  public static onMouseWheel(event: WheelEvent) {
    if (event.deltaY > 0)
      UIManager.canvas.onSystemMouseWheelDown(0, event.deltaY/2);
    else
      UIManager.canvas.onSystemMouseWheelUp(0, -event.deltaY/2);
  }

  public static onTouchStart(event: TouchEvent) {
    if (event.target != UIManager.htmlCanvas && event.target != document.body)
      return;

    if (!event.touches || event.touches.length != 1)
      return;

    var sx = (event.touches[0].clientX - UIManager.htmlCanvas.offsetLeft) * UIManager.canvasScale;
    var sy = (event.touches[0].clientY - UIManager.htmlCanvas.offsetTop) * UIManager.canvasScale;
    UIManager.canvas.onSystemMouseDown(sx, sy);

    event.preventDefault();
  }

  public static onTouchEnd(event: TouchEvent) {
    if (event.target != UIManager.htmlCanvas && event.target != document.body)
      return;

    if (!event.changedTouches || event.changedTouches.length != 1)
      return;

    var sx = (event.changedTouches[0].clientX - UIManager.htmlCanvas.offsetLeft) * UIManager.canvasScale;
    var sy = (event.changedTouches[0].clientY - UIManager.htmlCanvas.offsetTop) * UIManager.canvasScale;
    UIManager.canvas.onSystemMouseUp(sx, sy);

    event.preventDefault();
  }

  public static onTouchMove(event: TouchEvent) {
    if (event.target != UIManager.htmlCanvas && event.target != document.body)
      return;

    if (!event.touches || event.touches.length != 1)
      return;

    var sx = (event.touches[0].clientX - UIManager.htmlCanvas.offsetLeft) * UIManager.canvasScale;
    var sy = (event.touches[0].clientY - UIManager.htmlCanvas.offsetTop) * UIManager.canvasScale;
    UIManager.canvas.onSystemMouseMove(sx, sy);

    event.preventDefault();
  }
}